import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useFieldArray, useForm, type FieldErrors, type FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Plus, TriangleAlert, X } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Chip,
  Combobox,
  CopyableId,
  EntityDrawer,
  GeoSelect,
  Input,
  MacroInput,
  SegmentedControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TimezoneSelect,
  cn,
} from "@fox1ck-org/ui";
import { Form } from "@fox1ck-org/ui/form";
import { toast } from "@fox1ck-org/ui/toast";
import type { AffiliateNetwork } from "@gen/tracker/v1/affiliate_network_pb";
import { ConversionStatus } from "@gen/tracker/v1/conversion_pb";
import type { Offer } from "@gen/tracker/v1/offer_pb";

import { RhfField } from "@/components/rhf-field";
import { plural, useT, type TKey } from "@/lib/i18n";
import {
  SECTIONS,
  formProblems,
  formToOffer,
  mergeProblems,
  offerFormSchema,
  offerToForm,
  placeError,
  rowId,
  type OfferFormValues,
  type PlacedError,
  type Section,
} from "@/lib/schemas/offer";
import { LINK_MACROS, MODEL_HINT, OUR_STATUSES, OUR_STATUS_VIEW, PAYOUT_MODELS, modelCode } from "@/components/offers/labels";
import { useCheckOfferLink, useCreateOffer, useUpdateOffer, validateOffer } from "@/lib/services/offer-service";
import { useListPresets } from "@/lib/services/goal-service";
import { useReportTimezone } from "@/lib/services/workspace-service";
import { LinkCheckLine } from "@/components/offers/link-check";

/**
 * The offer editor (boards OfferEditor, OfferEditorErrors): one EntityDrawer,
 * six numbered sections, every value in its own box. «Сохранить» first asks
 * ValidateOffer for every problem at once and paints each under its field;
 * a section with none folds to one line so the broken ones are on screen.
 *
 * «Когда кап исчерпан» is a sentence, not a choice (owner, 21.10): a reached
 * cap only warns — traffic is neither stopped nor sent elsewhere.
 *
 * Not here, on purpose: «Группа» — `offers.group_id` and `offer_groups`
 * exist in Postgres, but no RPC lists, creates or sets a group (board field
 * blocked until one does); «Повторные конверсии» — a goal's, not an
 * offer's, setting.
 */

const DRAFT_KEY = "vibe-tracker.offer-draft";

const SECTION_TITLE: Record<Section, TKey> = {
  main: "u6.ed.sec.main",
  link: "u6.ed.sec.link",
  payout: "u6.ed.sec.payout",
  caps: "u6.ed.sec.caps",
  params: "u6.ed.sec.params",
  conversions: "u6.ed.sec.conversions",
};

export interface OfferEditorProps {
  open: boolean;
  onClose: () => void;
  /** The offer as read; undefined = a new one. */
  offer?: Offer;
  /** A new offer started from this one («Дублировать»). */
  seed?: Offer;
  networks: AffiliateNetwork[];
  onSaved: (offer: Offer) => void;
  /** Archives the offer (the footer's left action); absent = not offered. */
  onArchive?: () => void;
}

function readDraft(): OfferFormValues | undefined {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as OfferFormValues) : undefined;
  } catch {
    return undefined;
  }
}

function writeDraft(v: OfferFormValues | undefined) {
  try {
    if (v) localStorage.setItem(DRAFT_KEY, JSON.stringify(v));
    else localStorage.removeItem(DRAFT_KEY);
  } catch {
    // A private window has no storage: the draft just does not survive.
  }
}

export function OfferEditor({ open, onClose, offer, seed, networks, onSaved, onArchive }: OfferEditorProps) {
  const t = useT();
  const tz = useReportTimezone();
  const isNew = !offer;
  const locked = !!offer?.source;
  const initial = useMemo(() => {
    if (offer) return offerToForm(offer, { timezone: tz });
    if (seed) return offerToForm(seed, { timezone: tz });
    // No partner is picked for a person: the field is required, and a
    // preselected first one is how an offer lands at the wrong partner.
    return readDraft() ?? offerToForm(undefined, { timezone: tz });
  }, [offer, seed, tz]);

  const form = useForm<OfferFormValues>({
    resolver: zodResolver(offerFormSchema(t)),
    defaultValues: initial,
  });
  const [problems, setProblems] = useState<PlacedError[]>([]);
  const alertRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState<Set<Section>>(new Set());
  const create = useCreateOffer();
  const update = useUpdateOffer();

  useEffect(() => {
    if (!open) return;
    form.reset(initial);
    setProblems([]);
    setExpanded(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  // A new offer's draft keeps itself: closing the drawer loses nothing.
  useEffect(() => {
    if (!open || !isNew || seed) return;
    const sub = form.watch((v) => writeDraft(v as OfferFormValues));
    return () => sub.unsubscribe();
  }, [open, isNew, seed, form]);

  const networkId = form.watch("networkId");
  const network = networks.find((n) => n.id === networkId);
  const currency = network?.currency || offer?.payout?.amount?.currency || "USD";

  /** Every problem at once (board OfferEditorErrors): the service's, plus the form's own it did not name. */
  const showProblems = (placed: PlacedError[]) => {
    for (const p of placed) {
      if (p.field !== "root") form.setError(p.field as FieldPath<OfferFormValues>, { type: "server", message: p.message });
    }
    setProblems(placed);
    // The summary first: it names every broken field, the sections below show where.
    requestAnimationFrame(() => alertRef.current?.scrollIntoView({ block: "center" }));
  };

  const toOffer = (values: OfferFormValues) => {
    const next = formToOffer(values, offer ?? undefined, currency);
    if (seed && !offer) {
      next.id = "";
      next.source = "";
    }
    return next;
  };

  const onValid = async (values: OfferFormValues) => {
    const next = toOffer(values);
    const errors = await validateOffer(next);
    if (errors.length > 0) {
      showProblems(errors.map((e) => placeError(e, values)));
      return;
    }
    try {
      const saved = offer ? await update.mutateAsync(next) : await create.mutateAsync(next);
      if (isNew) writeDraft(undefined);
      toast.success(t(offer ? "u6.ed.saved" : "u6.ed.created"));
      onSaved(saved);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("u6.ed.saveFailed"));
    }
  };

  // The form's own check failed (no name, no partner): still ask the service,
  // so the summary lists the empty payout and the bad link beside them.
  const onInvalid = async (formErrors: FieldErrors<OfferFormValues>) => {
    const values = form.getValues();
    let placed: PlacedError[] = [];
    try {
      placed = (await validateOffer(toOffer(values))).map((e) => placeError(e, values));
    } catch {
      // The service is out of reach: the form's own problems are still true.
    }
    showProblems(mergeProblems(placed, formProblems(formErrors)));
  };

  const save = form.handleSubmit(onValid, onInvalid);

  // ⌘S saves, the way the footer says.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, save]);

  const bySection = useMemo(() => {
    const m = new Map<Section, PlacedError[]>();
    for (const p of problems) m.set(p.section, [...(m.get(p.section) ?? []), p]);
    return m;
  }, [problems]);
  const folded = (s: Section) => problems.length > 0 && !bySection.has(s) && !expanded.has(s);
  const busy = form.formState.isSubmitting;

  const title = offer ? t("u6.ed.title", { name: offer.name }) : t("u6.ed.titleNew");
  const description = offer ? t("u6.ed.sub") : t("u6.ed.subNew");

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={title}
      description={description}
      width="2xl"
      primaryAction={{ label: t("u6.ed.save"), onClick: () => void save(), loading: busy, loadingLabel: t("u6.ed.saving") }}
      secondaryAction={{ label: t("u6.common.cancel"), onClick: onClose }}
      destructiveAction={onArchive && !locked ? { label: t("u6.common.archive"), onClick: onArchive } : undefined}
    >
      <Form {...form}>
        <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="flex flex-col gap-4" aria-label={title}>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {offer ? <CopyableId value={offer.id} label={t("u6.offer.id")} truncate="end" /> : <Badge variant="info">{t("u6.ed.draft")}</Badge>}
            <span>{t("u6.ed.keys")}</span>
          </div>
          {locked ? (
            <Alert variant="info">
              <div className="flex min-w-0 flex-col gap-1">
                <AlertTitle>{t("u6.ed.sourced.title", { source: offer?.source ?? "" })}</AlertTitle>
                <AlertDescription>{t("u6.ed.sourced.body")}</AlertDescription>
              </div>
            </Alert>
          ) : null}
          {problems.length > 0 ? (
            <Alert ref={alertRef} variant="danger" role="alert">
              <TriangleAlert className="size-4" aria-hidden />
              <div className="flex min-w-0 flex-col gap-1">
                <AlertTitle>{t("u6.ed.err.title", { count: problems.length, word: plural(t("u6.ed.err.word"), problems.length) })}</AlertTitle>
                <AlertDescription>{problemSummary(problems, t)}</AlertDescription>
              </div>
            </Alert>
          ) : null}

          {SECTIONS.map((s, i) => (
            <EditorSection
              key={s}
              n={i + 1}
              title={t(SECTION_TITLE[s])}
              failed={bySection.has(s)}
              folded={folded(s)}
              summary={summaryOf(s, form.getValues(), network, t)}
              onExpand={() => setExpanded((prev) => new Set(prev).add(s))}
              rootErrors={(bySection.get(s) ?? []).filter((p) => p.field === "root")}
            >
              {s === "main" ? <MainSection form={form} networks={networks} locked={locked} /> : null}
              {s === "link" ? <LinkSection form={form} /> : null}
              {s === "payout" ? <PayoutSection form={form} currency={currency} locked={locked} /> : null}
              {s === "caps" ? <CapsSection form={form} /> : null}
              {s === "params" ? <ParamsSection form={form} locked={locked} /> : null}
              {s === "conversions" ? <ConversionsSection form={form} network={network} /> : null}
            </EditorSection>
          ))}
        </form>
      </Form>
    </EntityDrawer>
  );
}

type FormApi = ReturnType<typeof useForm<OfferFormValues>>;
type T = ReturnType<typeof useT>;

const FIELD_NAME: Record<string, TKey> = {
  name: "u6.ed.name",
  networkId: "u6.ed.network",
  countries: "u6.ed.countries",
  url: "u6.ed.url",
  payoutAmount: "u6.ed.amount",
  payoutModel: "u6.ed.model",
  dailyCap: "u6.ed.dailyCap",
  totalCap: "u6.ed.totalCap",
  capTimezone: "u6.ed.tz",
  goalPreset: "u6.ed.goal",
};

/** «Адрес ссылки, выплата, повтор страны в правилах» — what is wrong, by name. */
function problemSummary(problems: PlacedError[], t: T): string {
  const names = new Set<string>();
  for (const p of problems) {
    const head = p.field.split(".")[0] ?? "";
    if (head === "rules") names.add(t("u6.ed.err.rules"));
    else if (head === "params") names.add(t("u6.ed.err.params"));
    else if (head === "statuses") names.add(t("u6.ed.err.statuses"));
    else if (head === "url") names.add(t("u6.ed.err.url"));
    else if (FIELD_NAME[head]) names.add(t(FIELD_NAME[head]!).toLowerCase());
    else names.add(p.message);
  }
  const list = [...names].join(", ");
  return `${list.charAt(0).toUpperCase()}${list.slice(1)}. ${t("u6.ed.err.below")}`;
}

function summaryOf(s: Section, v: OfferFormValues, network: AffiliateNetwork | undefined, t: T): string {
  switch (s) {
    case "main":
      return [v.name, network?.name, v.countries.join(", ")].filter(Boolean).join(" · ");
    case "link":
      return v.url || "—";
    case "payout":
      return v.payoutAmount ? `${modelCode(Number(v.payoutModel))} ${v.payoutAmount} ${network?.currency ?? ""}`.trim() : "—";
    case "caps":
      return v.dailyCap ? t("u6.ed.sum.caps", { n: v.dailyCap }) : t("u6.ed.noLimit");
    case "params":
      return v.params.length ? v.params.map((p) => `${p.key}=${p.value}`).join(", ") : "—";
    case "conversions":
      return v.statuses.length ? t("u6.ed.sum.statuses", { n: v.statuses.length }) : t("u6.ed.sum.inherit");
  }
}

function EditorSection({
  n,
  title,
  failed,
  folded,
  summary,
  onExpand,
  rootErrors,
  children,
}: {
  n: number;
  title: string;
  failed: boolean;
  folded: boolean;
  summary: string;
  onExpand: () => void;
  rootErrors: PlacedError[];
  children: ReactNode;
}) {
  const t = useT();
  if (folded) {
    return (
      <section className="flex items-center gap-2 border-b border-border pb-3">
        <span className="text-xs tabular-nums text-muted-foreground">{n}</span>
        <h3 className="text-section-title">{title}</h3>
        <Check className="size-4 text-ok" aria-label={t("u6.ed.sectionOk")} />
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{summary}</span>
        <Button type="button" variant="ghost" size="sm" onClick={onExpand}>
          {t("u6.ed.expand")}
        </Button>
      </section>
    );
  }
  return (
    <section className="flex flex-col gap-3 border-b border-border pb-4" aria-label={title}>
      <div className="flex items-center gap-2">
        <span className="text-xs tabular-nums text-muted-foreground">{n}</span>
        <h3 className="text-section-title">{title}</h3>
        {failed ? <Badge variant="danger">{t("u6.ed.sectionErr")}</Badge> : null}
      </div>
      {rootErrors.map((e, i) => (
        <p key={i} className="text-xs text-destructive">
          {e.message}
        </p>
      ))}
      {children}
    </section>
  );
}

function MainSection({ form, networks, locked }: { form: FormApi; networks: AffiliateNetwork[]; locked: boolean }) {
  const t = useT();
  const c = form.control;
  const options = networks.map((n) => ({ value: n.id, label: `${n.name} · ${n.currency}` }));
  return (
    <>
      <RhfField control={c} name="name" label={t("u6.ed.name")} description={t("u6.ed.name.hint")} plain>
        {(field) => <Input {...field} disabled={locked} autoFocus={!locked} error={!!form.formState.errors.name} font="sans" />}
      </RhfField>
      <div className="grid grid-cols-2 gap-3">
        <RhfField control={c} name="networkId" label={t("u6.ed.network")} plain>
          {(field) => (
            <Combobox
              options={options}
              value={field.value}
              onChange={field.onChange}
              placeholder={t("u6.ed.network.pick")}
              searchPlaceholder={t("u6.ed.network.search")}
              emptyText={t("u6.common.nothing")}
              disabled={locked}
              label={t("u6.ed.network")}
            />
          )}
        </RhfField>
        <RhfField control={c} name="status" label={t("u6.ed.status")} plain>
          {(field) => (
            <SegmentedControl
              aria-label={t("u6.ed.status")}
              value={field.value}
              onValueChange={field.onChange}
              disabled={locked}
              options={[
                { value: "active", label: t("u6.status.active") },
                { value: "paused", label: t("u6.status.paused") },
              ]}
            />
          )}
        </RhfField>
      </div>
      <RhfField control={c} name="countries" label={t("u6.ed.countries")} plain>
        {(field) => <CountryList value={field.value} onChange={field.onChange} disabled={locked} />}
      </RhfField>
    </>
  );
}

/** Countries as chips, a new one picked from the list — never typed. */
export function CountryList({ value, onChange, disabled }: { value: string[]; onChange: (next: string[]) => void; disabled?: boolean }) {
  const t = useT();
  return (
    <div className="flex min-h-8 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-1.5 py-1">
      {value.map((code) => (
        <Chip key={code} label={code} disabled={disabled} onRemove={disabled ? undefined : () => onChange(value.filter((c) => c !== code))} />
      ))}
      {disabled ? null : (
        <GeoSelect
          kind="country"
          value=""
          onChange={(code) => code && !value.includes(code) && onChange([...value, code])}
          placeholder={t("u6.ed.countries.add")}
          locale="ru"
          className="h-6 w-auto min-w-40 border-0 bg-transparent px-1 shadow-none"
        />
      )}
    </div>
  );
}

function LinkSection({ form }: { form: FormApi }) {
  const t = useT();
  const c = form.control;
  const url = form.watch("url");
  const check = useCheckOfferLink();
  return (
    <>
      <RhfField control={c} name="url" label={t("u6.ed.url")} plain>
        {(field) => (
          <MacroInput
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            name={field.name}
            macros={LINK_MACROS}
            macrosLabel={t("u6.ed.url.macros")}
            error={!!form.formState.errors.url}
            placeholder="https://partner.example/c/…?click_id={click_id}"
            spellCheck={false}
          />
        )}
      </RhfField>
      <div className="flex items-center gap-2">
        <LinkCheckLine result={check.data} pending={check.isPending} />
        <div className="flex-1" />
        <Button type="button" variant="secondary" size="sm" disabled={!url.trim() || check.isPending} onClick={() => check.mutate(url.trim())}>
          {t("u6.link.check")}
        </Button>
      </div>
      <RhfField control={c} name="redirect" label={t("u6.ed.how")} plain>
        {(field) => (
          <SegmentedControl
            aria-label={t("u6.ed.how")}
            value={field.value ? "redirect" : "link"}
            onValueChange={(v) => field.onChange(v === "redirect")}
            options={[
              { value: "redirect", label: t("u6.ed.how.redirect") },
              { value: "link", label: t("u6.ed.how.link") },
            ]}
          />
        )}
      </RhfField>
    </>
  );
}

function ModelSelect({ value, onChange, disabled, short, label }: { value: string; onChange: (v: string) => void; disabled?: boolean; short?: boolean; label: string }) {
  const t = useT();
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PAYOUT_MODELS.map((m) => (
          <SelectItem key={m} value={String(m)}>
            {short || !MODEL_HINT[m] ? modelCode(m) : `${modelCode(m)} — ${t(MODEL_HINT[m]!)}`}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MoneyInput({ value, onChange, onBlur, currency, error, disabled, label }: { value: string; onChange: (v: string) => void; onBlur?: () => void; currency: string; error?: boolean; disabled?: boolean; label: string }) {
  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        inputMode="decimal"
        error={error}
        disabled={disabled}
        aria-label={label}
        className="pr-12 font-sans tabular-nums"
      />
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">{currency}</span>
    </div>
  );
}

function PayoutSection({ form, currency, locked }: { form: FormApi; currency: string; locked: boolean }) {
  const t = useT();
  const c = form.control;
  const rules = useFieldArray({ control: c, name: "rules", keyName: "key" });
  const errors = form.formState.errors.rules;
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <RhfField control={c} name="payoutModel" label={t("u6.ed.model")} plain>
          {(field) => <ModelSelect value={field.value} onChange={field.onChange} disabled={locked} label={t("u6.ed.model")} />}
        </RhfField>
        <RhfField control={c} name="payoutAmount" label={t("u6.ed.amount")} plain>
          {(field) => (
            <MoneyInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} currency={currency} error={!!form.formState.errors.payoutAmount} disabled={locked} label={t("u6.ed.amount")} />
          )}
        </RhfField>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex text-xs text-muted-foreground">
          <span>{t("u6.ed.rules")}</span>
          <div className="flex-1" />
          <span>{t("u6.ed.rules.rest")}</span>
        </div>
        {rules.fields.map((r, i) => {
          const e = errors?.[i];
          const message = e?.country?.message ?? e?.amount?.message ?? e?.model?.message;
          return (
            <div key={r.key} className="flex flex-col gap-1">
              <div className="grid grid-cols-[1fr_--spacing(27.5)_--spacing(32.5)_--spacing(7)] items-center gap-2">
                <GeoSelect
                  kind="country"
                  locale="ru"
                  value={form.watch(`rules.${i}.country`)}
                  onChange={(v) => form.setValue(`rules.${i}.country`, v, { shouldDirty: true })}
                  disabled={locked}
                  placeholder={t("u6.ed.rules.country")}
                  className={cn(e?.country && "border-destructive")}
                />
                <ModelSelect short value={form.watch(`rules.${i}.model`)} onChange={(v) => form.setValue(`rules.${i}.model`, v, { shouldDirty: true })} disabled={locked} label={t("u6.ed.model")} />
                <MoneyInput
                  value={form.watch(`rules.${i}.amount`)}
                  onChange={(v) => form.setValue(`rules.${i}.amount`, v, { shouldDirty: true })}
                  currency={currency}
                  error={!!e?.amount}
                  disabled={locked}
                  label={t("u6.ed.amount")}
                />
                {locked ? <span /> : (
                  <Button type="button" variant="ghost" size="sm" className="size-7 px-0" aria-label={t("u6.ed.rules.remove")} onClick={() => rules.remove(i)}>
                    <X className="size-3.5" />
                  </Button>
                )}
              </div>
              {message ? <span className="text-xs text-destructive">{message}</span> : null}
            </div>
          );
        })}
        {locked ? null : (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="self-start"
            onClick={() => rules.append({ id: rowId(), country: "", model: form.getValues("payoutModel"), amount: "" })}
          >
            <Plus className="size-3.5" /> {t("u6.ed.rules.add")}
          </Button>
        )}
      </div>
    </>
  );
}

function CapsSection({ form }: { form: FormApi }) {
  const t = useT();
  const c = form.control;
  return (
    <div className="grid grid-cols-2 gap-3">
      <RhfField control={c} name="dailyCap" label={t("u6.ed.dailyCap")} plain>
        {(field) => <Input {...field} inputMode="numeric" placeholder={t("u6.ed.noLimit")} className="tabular-nums" />}
      </RhfField>
      <RhfField control={c} name="totalCap" label={t("u6.ed.totalCap")} plain>
        {(field) => <Input {...field} inputMode="numeric" placeholder={t("u6.ed.noLimit")} className="tabular-nums" />}
      </RhfField>
      <RhfField control={c} name="capTimezone" label={t("u6.ed.tz")} description={t("u6.ed.tz.hint")} plain>
        {(field) => <TimezoneSelect value={field.value} onChange={field.onChange} aria-label={t("u6.ed.tz")} />}
      </RhfField>
      <div className="flex flex-col gap-1.5">
        <span className="text-sm text-muted-foreground">{t("u6.ed.onCap")}</span>
        <p className="py-1.5 text-xs text-muted-foreground">{t("u6.ed.onCap.value")}</p>
      </div>
    </div>
  );
}

function ParamsSection({ form, locked }: { form: FormApi; locked: boolean }) {
  const t = useT();
  const params = useFieldArray({ control: form.control, name: "params", keyName: "key_" });
  const errors = form.formState.errors.params;
  return (
    <>
      <span className="text-xs text-muted-foreground">{t("u6.ed.params.hint")}</span>
      {params.fields.map((p, i) => (
        <div key={p.key_} className="flex flex-col gap-1">
          <div className="grid grid-cols-[--spacing(45)_1fr_--spacing(7)] gap-2">
            <Input {...form.register(`params.${i}.key`)} placeholder={t("u6.ed.params.key")} aria-label={t("u6.ed.params.key")} error={!!errors?.[i]?.key} disabled={locked} spellCheck={false} />
            <Input {...form.register(`params.${i}.value`)} placeholder={t("u6.ed.params.value")} aria-label={t("u6.ed.params.value")} disabled={locked} spellCheck={false} />
            {locked ? <span /> : (
              <Button type="button" variant="ghost" size="sm" className="size-7 px-0" aria-label={t("u6.ed.params.remove")} onClick={() => params.remove(i)}>
                <X className="size-3.5" />
              </Button>
            )}
          </div>
          {errors?.[i]?.key?.message ? <span className="text-xs text-destructive">{errors[i]!.key!.message}</span> : null}
        </div>
      ))}
      {locked ? null : (
        <Button type="button" variant="secondary" size="sm" className="self-start" onClick={() => params.append({ id: rowId(), key: "", value: "" })}>
          <Plus className="size-3.5" /> {t("u6.ed.params.add")}
        </Button>
      )}
    </>
  );
}

function ConversionsSection({ form, network }: { form: FormApi; network: AffiliateNetwork | undefined }) {
  const t = useT();
  const c = form.control;
  const { data: presets } = useListPresets();
  const statuses = useFieldArray({ control: c, name: "statuses", keyName: "key_" });
  const errors = form.formState.errors.statuses;
  const inherited = network?.conversionMapping?.statuses ?? [];
  return (
    <>
      <RhfField control={c} name="goalPreset" label={t("u6.ed.goal")} plain>
        {(field) => (
          <Select value={field.value || "__default"} onValueChange={(v) => field.onChange(v === "__default" ? "" : v)}>
            <SelectTrigger aria-label={t("u6.ed.goal")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default">{t("u6.ed.goal.default")}</SelectItem>
              {(presets ?? []).map((p) => (
                <SelectItem key={p.key} value={p.key}>
                  {p.displayName || p.key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </RhfField>
      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <span className="min-w-0 flex-1">
          {network
            ? t("u6.ed.statuses.inherit", {
                network: network.name,
                words: inherited.length ? inherited.slice(0, 4).map((s) => `${s.word} → ${t(OUR_STATUS_VIEW[s.status].label).toLowerCase()}`).join(", ") : t("u6.ed.statuses.global"),
              })
            : null}
        </span>
        {network ? (
          <Button asChild variant="secondary" size="sm">
            <Link to={`/library/networks/${network.id}`}>{t("u6.ed.statuses.atPartner")}</Link>
          </Button>
        ) : null}
      </div>
      <span className="text-xs text-muted-foreground">{t("u6.ed.statuses.own")}</span>
      {statuses.fields.map((s, i) => (
        <div key={s.key_} className="flex flex-col gap-1">
          <div className="grid grid-cols-[1fr_--spacing(45)_--spacing(7)] gap-2">
            <Input {...form.register(`statuses.${i}.word`)} placeholder={t("u6.ed.statuses.word")} aria-label={t("u6.ed.statuses.word")} error={!!errors?.[i]?.word} className="font-mono" spellCheck={false} />
            <Select value={form.watch(`statuses.${i}.status`)} onValueChange={(v) => form.setValue(`statuses.${i}.status`, v, { shouldDirty: true })}>
              <SelectTrigger aria-label={t("u6.ed.statuses.ours")} className={cn(errors?.[i]?.status && "border-destructive")}>
                <SelectValue placeholder={t("u6.common.pick")} />
              </SelectTrigger>
              <SelectContent>
                {OUR_STATUSES.map((st) => (
                  <SelectItem key={st} value={String(st)}>
                    {t(OUR_STATUS_VIEW[st].label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="button" variant="ghost" size="sm" className="size-7 px-0" aria-label={t("u6.ed.statuses.remove")} onClick={() => statuses.remove(i)}>
              <X className="size-3.5" />
            </Button>
          </div>
          {errors?.[i]?.word?.message || errors?.[i]?.status?.message ? (
            <span className="text-xs text-destructive">{errors?.[i]?.word?.message ?? errors?.[i]?.status?.message}</span>
          ) : null}
        </div>
      ))}
      <Button type="button" variant="secondary" size="sm" className="self-start" onClick={() => statuses.append({ id: rowId(), word: "", status: String(ConversionStatus.APPROVED) })}>
        <Plus className="size-3.5" /> {t("u6.ed.statuses.add")}
      </Button>
    </>
  );
}
